/**
 * Passives — persistent modifiers acquired during a run.
 *
 * See `docs/GEO-SLEEPER-passives.md` for the design doc.
 *
 * Templates are deliberately neutral ("the operative", not "the player") so
 * the same pool can later be used to build agent traits — see the Passives
 * as Agent Traits section of the design doc.
 *
 * This file is pure data. Effects are implemented at each passive's natural
 * integration point throughout the engine, gated by `hasPassive(run, key)`.
 */

export type PassiveBucket =
  | 'training'
  | 'background'
  | 'contacts'
  | 'loadout'
  | 'perception'
  | 'economy'
  | 'combat';

export interface PassiveTemplate {
  key: string;
  name: string;
  bucket: PassiveBucket;
  description: string;
  /** Biases the run-start draft pool. Loadout and broad utility passives
   *  are marked starter-friendly; build-around passives that need a specific
   *  deck to function are not. */
  starterFriendly?: boolean;
}

export const PASSIVE_TEMPLATES: Record<string, PassiveTemplate> = {
  // --- Training ---
  tactician: {
    key: 'tactician',
    name: 'Tactician',
    bucket: 'training',
    description:
      'Tactics cards cost 1 less AP, with no minimum. Decoy, Hide Inside, Hotwire, Misdirect, and Rooftop Access all become significantly more spammable.',
  },
  surveillanceState: {
    key: 'surveillanceState',
    name: 'Surveillance State',
    bucket: 'training',
    description:
      'Intel cards cost 1 less AP, with no minimum. Enables intel-heavy hunter builds.',
  },
  kravMaga: {
    key: 'kravMaga',
    name: 'Krav Maga',
    bucket: 'training',
    description:
      'Improvised Strike costs 0 AP. Stack Prep aggressively without burning AP.',
  },
  operationalTempo: {
    key: 'operationalTempo',
    name: 'Operational Tempo',
    bucket: 'training',
    description:
      'Fieldcraft cards cost 1 less AP, with no minimum. Build-defining for combo decks.',
  },
  capacious: {
    key: 'capacious',
    name: 'Capacious',
    bucket: 'training',
    description:
      'Maximum hand size +1, permanently. Smoother cycling, more options each turn.',
    starterFriendly: true,
  },
  longStride: {
    key: 'longStride',
    name: 'Long Stride',
    bucket: 'training',
    description:
      'All movement cards grant +50m extra range.',
    starterFriendly: true,
  },
  coolHead: {
    key: 'coolHead',
    name: 'Cool Head',
    bucket: 'training',
    description:
      'Start every round with +20% Prep already accumulated. The first ambush or gun confrontation lands harder without setup.',
    starterFriendly: true,
  },
  persistentSurveillance: {
    key: 'persistentSurveillance',
    name: 'Persistent Surveillance',
    bucket: 'training',
    description:
      'All intel card effects (Sweep, Spotter, Wire Tap, Trace Route) last one extra turn before decaying or expiring.',
  },
  hiddenPocket: {
    key: 'hiddenPocket',
    name: 'Hidden Pocket',
    bucket: 'training',
    description:
      'If you have exactly 1 card in your hand at end of turn, it is retained instead of discarded.',
    starterFriendly: true,
  },
  headhunter: {
    key: 'headhunter',
    name: 'Headhunter',
    bucket: 'training',
    description:
      'After playing an intel card, the next intel card you play that turn costs 1 less AP. Rewards intel-chaining within a single turn.',
  },

  // --- Background ---
  // Deep Cover has been removed from the player passive pool and moved
  // to an agent-only passive — see src/data/agentPassives.ts. The agent
  // version inverts the effect: when an agent rolls Deep Cover, the
  // PLAYER'S vision bonuses (binoculars, rooftop) stop working against
  // that specific agent.
  subwaySurfer: {
    key: 'subwaySurfer',
    name: 'Subway Surfer',
    bucket: 'background',
    description:
      'Transit entry and exit read as normal movement signal, not a confirmed station ping. You can still be tracked along a transit corridor.',
  },
  bystander: {
    key: 'bystander',
    name: 'Bystander',
    bucket: 'background',
    description:
      'Signal generated in commercial zones reduced by 40%. Crowd cover gets even better when you know how to use it.',
    starterFriendly: true,
  },
  neighbor: {
    key: 'neighbor',
    name: 'Neighbor',
    bucket: 'background',
    description:
      'Signal generated in residential zones reduced by 25%. Walking your own neighborhood barely registers.',
    starterFriendly: true,
  },
  burnedFace: {
    key: 'burnedFace',
    name: 'Burned Face',
    bucket: 'background',
    description:
      'Generates 25% less signal on every road type. Broad, flat reduction — works in any city.',
    starterFriendly: true,
  },
  firstMoverAdvantage: {
    key: 'firstMoverAdvantage',
    name: 'First Mover Advantage',
    bucket: 'background',
    description:
      'The first signal generated each turn is reduced by 50%. Spend your first action wisely.',
  },
  methodActor: {
    key: 'methodActor',
    name: 'Method Actor',
    bucket: 'background',
    description:
      'Misdirect generates signal 25% stronger (tighter, more convincing fake circles). Build-around for active-deception play.',
  },
  freeRunner: {
    key: 'freeRunner',
    name: 'Free Runner',
    bucket: 'background',
    description:
      'Rooftop Access costs 1 less AP and can climb any building, not just commercial. Removes elevation gating on residential-heavy maps.',
  },
  wheelman: {
    key: 'wheelman',
    name: 'Wheelman',
    bucket: 'background',
    description:
      'Hotwire costs 1 less AP. While in a vehicle, signal generation is reduced by 25%.',
  },

  // --- Contacts ---
  coldExit: {
    key: 'coldExit',
    name: 'Cold Exit',
    bucket: 'contacts',
    description:
      'After leaving a safehouse, generate zero signal for 3 turns regardless of action. The trail goes cold the moment you step outside.',
  },
  quartermaster: {
    key: 'quartermaster',
    name: 'Quartermaster',
    bucket: 'contacts',
    description:
      'More weapon caches spawn on the map. Weapons also have higher roll odds in mixed supply drops.',
    starterFriendly: true,
  },
  gadgetGuy: {
    key: 'gadgetGuy',
    name: 'Gadget Guy',
    bucket: 'contacts',
    description:
      'More gadget stashes spawn on the map. Gadgets also have higher roll odds in mixed supply drops.',
    starterFriendly: true,
  },
  locksmithsNephew: {
    key: 'locksmithsNephew',
    name: "Locksmith's Nephew",
    bucket: 'loadout',
    description: 'Start each round with a Lock Pick in your inventory.',
    starterFriendly: true,
  },
  cryptologist: {
    key: 'cryptologist',
    name: 'Cryptologist',
    bucket: 'contacts',
    description:
      'One of the two required passphrases is already in your possession at round start.',
  },
  blackMarketConnect: {
    key: 'blackMarketConnect',
    name: 'Black Market Connect',
    bucket: 'contacts',
    description:
      '10 additional item pickup locations are added to the map at round start. Build-defining for scavenger runs.',
  },
  resourceful: {
    key: 'resourceful',
    name: 'Resourceful',
    bucket: 'contacts',
    description:
      '+1 inventory slot (carry 5 items instead of 4). Hoard for extract value or carry a heavier toolkit into a fight.',
    starterFriendly: true,
  },

  // --- Loadout ---
  concealedCarry: {
    key: 'concealedCarry',
    name: 'Concealed Carry',
    bucket: 'loadout',
    description: 'Start each round with a Pistol in your inventory.',
    starterFriendly: true,
  },
  edgeInTheBoot: {
    key: 'edgeInTheBoot',
    name: 'Edge in the Boot',
    bucket: 'loadout',
    description: 'Start each round with a Combat Knife in your inventory.',
    starterFriendly: true,
  },
  overwatchPackage: {
    key: 'overwatchPackage',
    name: 'Overwatch Package',
    bucket: 'loadout',
    description:
      'Start each round with a Sniper Rifle in your inventory. Only useful at elevation — pairs with rooftop-focused decks.',
  },

  // --- Perception ---
  echoChamber: {
    key: 'echoChamber',
    name: 'Echo Chamber',
    bucket: 'perception',
    description:
      'Reveals agent passive signals on the map at 25% stronger precision. Without this or a Radio Scanner, you are blind to the agent\'s ambient movement signal.',
  },
  coldOpening: {
    key: 'coldOpening',
    name: 'Cold Opening',
    bucket: 'perception',
    description:
      'On turn 1 of every round, the agent\'s exact starting position is revealed (Spotter-style, 1-turn duration).',
    starterFriendly: true,
  },
  blownEntrance: {
    key: 'blownEntrance',
    name: 'Blown Entrance',
    bucket: 'perception',
    description:
      'Reveals agent passive signals for the first 3 turns of every round at 75% stronger precision. Gives you a head start on locating the agent before going blind.',
  },

  // --- Economy ---
  bagMan: {
    key: 'bagMan',
    name: 'Bag Man',
    bucket: 'economy',
    description:
      'All Marks earned during a round are increased by 25%. Does not affect item extract values.',
    starterFriendly: true,
  },
  severance: {
    // Key stays `severance` so saved runs keep working without migration —
    // the display name is what the player sees.
    key: 'severance',
    name: 'Spoiled for Choice',
    bucket: 'economy',
    description:
      'HQ card packs and extraction-op rewards show 5 cards instead of 3. Wider draft = better odds of finding the card you want.',
    starterFriendly: true,
  },
  bankJob: {
    key: 'bankJob',
    name: 'Bank Job',
    bucket: 'economy',
    description:
      'Item extract values are increased by 50%. Build-around for the extract-and-sell strategy.',
  },

  // --- Combat ---
  hardTarget: {
    key: 'hardTarget',
    name: 'Hard Target',
    bucket: 'combat',
    description:
      'In any Ambushed scenario, automatically have one Flashbang\'s worth of escape — even without a Flashbang in inventory. Once per round.',
    starterFriendly: true,
  },
};

/** Cached list of all passive keys for pool sampling. */
export const ALL_PASSIVE_KEYS: string[] = Object.keys(PASSIVE_TEMPLATES);

/** Passives eligible for the run-start draft (starter-friendly subset). */
export const STARTER_PASSIVE_KEYS: string[] = ALL_PASSIVE_KEYS.filter(
  k => PASSIVE_TEMPLATES[k].starterFriendly,
);

/** Number of passive options presented at each draft. */
export const PASSIVE_DRAFT_POOL_SIZE = 3;

/**
 * Check whether an owner (player run, or later, an agent build) has a
 * given passive. Lives in the data module so engine code can check without
 * importing from runState.ts (avoiding circular deps between runState and
 * passiveEffects).
 *
 * Accepts any object with a `passives: string[]` field — the parameter is
 * typed as `{ passives?: string[] } | null | undefined` rather than
 * `RunState` specifically, so the same helper can be called on a future
 * agent-owner structure without coupling to run state.
 */
export function hasPassive(
  owner: { passives?: string[] } | null | undefined,
  key: string,
): boolean {
  return !!owner?.passives?.includes(key);
}
